import React, { useState, useEffect, Suspense, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Toast } from "primereact/toast";
import ComplaintService from '../../services/complaint/ComplaintService';
import AppLoading from '../app-loading/app-loading';
import InternalComplaintDataTable from './InternalComplaintDataTable';
import InternalComplaintSearch from './InternalComplaintSearch';

const InternalComplaintMain: React.FC = () => {
  const toast = useRef<Toast>(null);
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [filteredComplaints, setFilteredComplaints] = useState<any[]>([]);
  const [complaintList, setComplaintList] = useState<any[]>([]);
  const [pageNo, setPageNo] = useState(1);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState<any>({});

  const loadComplaints = async () => {
    setLoading(true);
    try {
      const response = await ComplaintService.GetAllInternalComplaints();
      let initialFiltered = [...response];

      if (filters.startDate && filters.endDate) {
        initialFiltered = initialFiltered.filter(complaint => {
          const createdDate = new Date(complaint.createdDate || '');
          return createdDate >= filters.startDate && createdDate <= filters.endDate;
        });
      }

      if (filters.searchKeyword) {
        const searchTerm = filters.searchKeyword.toLowerCase();
        initialFiltered = initialFiltered.filter(complaint =>
          Object.values(complaint).some(value =>
            value?.toString().toLowerCase().includes(searchTerm)
          )
        );
      }

      setComplaintList(response);
      setFilteredComplaints(initialFiltered);
      setTotalRecords(initialFiltered.length);
    } catch (error) {
      console.error('Error loading complaints:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load complaints',
        life: 3000
      });
      setComplaintList([]);
      setFilteredComplaints([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadComplaints();
  }, [pageNo, pageSize, filters]);

  const handleSearch = (searchFilters: any) => {
    setPageNo(1);
    setFilters(searchFilters);
  };

  const handlePageChange = (event: any) => {
    const newPageNumber = Math.floor(event.first / event.rows) + 1;
    setPageNo(newPageNumber);
    setPageSize(event.rows);
  };

  const handleComplaintUpdate = () => {
    loadComplaints();
  };

  return (
    <>
      <Suspense fallback={<AppLoading />}>
        <Toast ref={toast} position="top-center" />
        <div className="layout-content mt-3 mb-3 grid grid-table-mobile">
          <div className="col-12">
            <div className="flex align-items-center justify-content-between flex-wrap gap-2 w-full card-container mb-0">
              <h5 className="ml-1 mb-0">{t("Internal Complaints for QA")}</h5>
            </div>
            <InternalComplaintSearch
              onSearch={handleSearch}
              setLoading={setLoading}
            />
          </div>
          <InternalComplaintDataTable
            data={filteredComplaints}
            pageNo={pageNo}
            pageSize={pageSize}
            handlepagechange={handlePageChange}
            totalRecordsCount={totalRecords}
            onComplaintUpdate={handleComplaintUpdate}
          />
        </div>
        {loading && <AppLoading />}
      </Suspense>
    </>
  );
};

export default InternalComplaintMain;